import { Button, Input, Spinner, Switch } from '@heroui/react';
import { Search } from 'lucide-react';
import React, { FC, useState } from 'react';

import { StockPhoto, useSearchStockPhotos } from '../../../../queries';
import { useAdsGeneratorStore } from '../../model/store';
import { AssetGrid, AssetThumb, AssetTile, EmptyPanelHint, TileBadge } from './Editor.styled';

interface StockSearchGridProps {
  onPick: (photo: StockPhoto) => void;
  /** Fraza, od której startuje wyszukiwarka po otwarciu panelu. */
  initialTerm?: string;
}

/** Orientacja wyników dobrana do proporcji planszy, żeby kadr pasował bez przycinania. */
const canvasOrientation = (width: number, height: number): string => {
  const ratio = width / height;
  if (ratio > 1.6) return 'panoramic';
  if (ratio > 1.1) return 'landscape';
  if (ratio < 0.9) return 'portrait';
  return 'square';
};

/**
 * Wyszukiwarka banku zdjęć wspólna dla panelu Magnific i dla wyboru tła.
 *
 * Pokazuje wyłącznie podglądy z CDN Magnifica i NICZEGO nie pobiera — kliknięcie
 * kafelka tylko oddaje wybrane zdjęcie rodzicowi, który pyta o potwierdzenie.
 * Pulę pobrań zabiera dopiero import.
 */
export const StockSearchGrid: FC<StockSearchGridProps> = ({ onPick, initialTerm = '' }) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const [term, setTerm] = useState(initialTerm);
  const [submitted, setSubmitted] = useState(initialTerm);
  // Konto premium pobiera także pozycje premium, więc domyślnie pokazujemy całą
  // bibliotekę. Przełącznik zostaje na wypadek odmowy przy konkretnym zasobie.
  const [freeOnly, setFreeOnly] = useState(false);

  const orientation = scene ? canvasOrientation(scene.width, scene.height) : undefined;
  const { data, isFetching, error } = useSearchStockPhotos(
    { term: submitted, orientation, freeOnly },
    submitted.length > 1,
  );

  const submit = (): void => setSubmitted(term.trim());

  return (
    <>
      <div className="flex gap-1">
        <Input
          size="sm"
          radius="sm"
          className="min-w-0"
          placeholder="Czego szukasz?"
          value={term}
          onValueChange={setTerm}
          onKeyDown={(event) => {
            if (event.key === 'Enter') submit();
          }}
        />
        <Button
          size="sm"
          radius="sm"
          isIconOnly
          className="bg-black text-white"
          aria-label="Szukaj"
          isLoading={isFetching}
          onPress={submit}
        >
          <Search size={16} />
        </Button>
      </div>

      <Switch size="sm" isSelected={freeOnly} onValueChange={setFreeOnly}>
        <span className="text-xs">Tylko darmowe (freemium)</span>
      </Switch>

      {Boolean(error) && <EmptyPanelHint>{error?.message || 'Bank zdjęć nie odpowiedział'}</EmptyPanelHint>}

      {!isFetching && !error && data && data.items.length === 0 && (
        <EmptyPanelHint>Brak wyników dla tej frazy.</EmptyPanelHint>
      )}

      {isFetching && !data && <Spinner size="sm" />}

      {data && data.items.length > 0 && (
        <AssetGrid>
          {data.items.map((photo) => (
            <AssetTile key={photo.id}>
              <AssetThumb
                type="button"
                title={`${photo.title}${photo.author ? `, autor: ${photo.author}` : ''}`}
                onClick={() => onPick(photo)}
              >
                <img src={photo.previewUrl} alt="" />
              </AssetThumb>
              {photo.license === 'premium' && <TileBadge>premium</TileBadge>}
            </AssetTile>
          ))}
        </AssetGrid>
      )}
    </>
  );
};
